let initialized = false;

const TRACK_ENDPOINT = '/track';
const SESSION_COOKIE = 'sw_session_id';

const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value, days = 395) => {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
};

const generateSessionId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const ensureSessionId = () => {
    const existing = readCookie(SESSION_COOKIE) || window.localStorage.getItem(SESSION_COOKIE);

    if (existing) {
        writeCookie(SESSION_COOKIE, existing);
        window.localStorage.setItem(SESSION_COOKIE, existing);
        return existing;
    }

    const fresh = generateSessionId();
    writeCookie(SESSION_COOKIE, fresh);
    window.localStorage.setItem(SESSION_COOKIE, fresh);
    return fresh;
};

const safeUrl = (value) => {
    if (!value) return undefined;
    try {
        const url = new URL(value, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return undefined;
        const allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
        const safe = new URL(url.origin + url.pathname);
        for (const key of allowed) {
            const entry = url.searchParams.get(key);
            if (entry) safe.searchParams.set(key, entry);
        }
        return safe.toString();
    } catch {
        return undefined;
    }
};

const safeText = (value) => {
    if (typeof value !== 'string') return value;
    if (/[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}/u.test(value)
        || /(?:\+|00)?\d[\d\s()./-]{5,}\d/u.test(value)) {
        return '[redacted]';
    }
    return value;
};

const sanitizeMetadata = (input) => {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
    const sensitive = /(?:^|_)(?:email|phone|mobile|first_name|last_name|full_name|street|house_number|address|notes?|token|lead_id|partial_lead_id|resume_token|consent_text)(?:$|_)/i;
    return Object.fromEntries(Object.entries(input).flatMap(([key, value]) => {
        if (sensitive.test(key)) return [];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return [[key, sanitizeMetadata(value)]];
        }
        return [[key, safeText(value)]];
    }));
};

const currentPath = () => window.location.pathname;

const getUtm = () => {
    const params = new URLSearchParams(window.location.search);

    return {
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_term: params.get('utm_term') || undefined,
        utm_content: params.get('utm_content') || undefined,
    };
};

const detectTrafficSource = () => {
    const params = new URLSearchParams(window.location.search);

    // Ad click IDs take highest priority
    if (params.get('gclid') || params.get('gad_source')) return 'google_ads';
    if (params.get('fbclid')) return 'meta_ads';
    if (params.get('rdt_cid')) return 'reddit_ads';
    if (params.get('msclkid')) return 'bing_ads';
    if (params.get('twclid')) return 'twitter_ads';
    if (params.get('ttclid')) return 'tiktok_ads';
    if (params.get('li_fat_id')) return 'linkedin_ads';
    if (params.get('epik')) return 'pinterest_ads';

    const src = (params.get('utm_source') || '').toLowerCase();
    const med = (params.get('utm_medium') || '').toLowerCase();

    const isPaid = ['cpc', 'cpm', 'ppc', 'paid', 'paid_social', 'display', 'retargeting', 'paidsocial'].includes(med)
        || med.includes('paid') || med.includes('ad');

    if (src || med) {
        if (isPaid) {
            if (['google', 'google_ads', 'googleads', 'adwords', 'gads'].includes(src)) return 'google_ads';
            if (['facebook', 'fb', 'instagram', 'ig', 'meta', 'fb_ads', 'facebook_ads', 'meta_ads'].includes(src)) return 'meta_ads';
            if (['reddit', 'reddit_ads'].includes(src)) return 'reddit_ads';
            if (['twitter', 'x', 'twitter_ads', 'x_ads'].includes(src)) return 'twitter_ads';
            if (['bing', 'bing_ads', 'microsoft', 'msn', 'microsoft_ads'].includes(src)) return 'bing_ads';
            if (['tiktok', 'tiktok_ads'].includes(src)) return 'tiktok_ads';
            if (['linkedin', 'linkedin_ads'].includes(src)) return 'linkedin_ads';
            if (['pinterest', 'pinterest_ads'].includes(src)) return 'pinterest_ads';
            return 'paid_other';
        }
        if (['email', 'newsletter'].includes(med)) return 'email';
        if (['social', 'social-media', 'organic_social'].includes(med) || ['facebook', 'fb', 'instagram', 'twitter', 'x', 'linkedin', 'reddit', 'tiktok', 'pinterest'].includes(src)) return 'organic_social';
        if (med === 'referral') return 'referral';
        return 'utm_other';
    }

    const ref = document.referrer;
    if (!ref) return 'direct';

    try {
        const host = new URL(ref).hostname.toLowerCase();
        if (host.includes('google.')) return 'organic_google';
        if (host.includes('bing.com')) return 'organic_bing';
        if (['yahoo.com', 'duckduckgo.com', 'ecosia.org', 'baidu.com'].some(d => host.includes(d))) return 'organic_other';
        const socialDomains = ['facebook.com', 'fb.com', 'instagram.com', 'twitter.com', 'x.com', 't.co', 'reddit.com', 'linkedin.com', 'tiktok.com', 'pinterest.com', 'youtube.com', 'youtu.be'];
        if (socialDomains.some(d => host.includes(d))) return 'organic_social';
        return 'referral';
    } catch {
        return 'direct';
    }
};

const toGaEventName = (eventName) => String(eventName || 'event').replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();

const sendToGa = (eventName, payload = {}) => {
    if (typeof window.gtag !== 'function') {
        return;
    }

    window.gtag('event', toGaEventName(eventName), {
        event_category: payload.event_category,
        page_path: payload.page_path,
        page_location: payload.page_url,
        link_url: payload.element_href,
        link_text: payload.element_text,
        service_type: payload.service_type,
        city_slug: payload.city_slug,
        location_slug: payload.location_slug,
        design_variant_family: payload.design_variant_family,
        funnel_variant_family: payload.funnel_variant_family,
        value: payload.value,
        transport_type: 'beacon',
    });
};

export const trackEvent = async (eventName, payload = {}) => {
    const sessionId = ensureSessionId();

    const body = {
        event_name: eventName,
        event_category: payload.event_category,
        page_path: payload.page_path ? (safeUrl(payload.page_path) ? new URL(safeUrl(payload.page_path)).pathname : currentPath()) : currentPath(),
        page_url: safeUrl(payload.page_url || window.location.href),
        referrer: safeUrl(payload.referrer ?? (document.referrer || undefined)),
        service_type: payload.service_type,
        city_slug: payload.city_slug,
        location_id: payload.location_id,
        location_slug: payload.location_slug,
        location_name: payload.location_name,
        design_variant_id: payload.design_variant_id,
        design_variant_family: payload.design_variant_family,
        design_variant_name: payload.design_variant_name,
        funnel_variant_id: payload.funnel_variant_id,
        funnel_variant_family: payload.funnel_variant_family,
        funnel_variant_name: payload.funnel_variant_name,
        element_tag: payload.element_tag,
        element_id: payload.element_id,
        element_class: payload.element_class,
        element_text: safeText(payload.element_text),
        element_href: safeUrl(payload.element_href),
        value: payload.value,
        metadata: {
            viewport_w: window.innerWidth,
            viewport_h: window.innerHeight,
            language: navigator.language,
            ...sanitizeMetadata(payload.metadata),
        },
        traffic_source: payload.traffic_source || detectTrafficSource(),
        occurred_at: new Date().toISOString(),
        session_id: sessionId,
        ...getUtm(),
    };

    sendToGa(eventName, body);

    const jsonBody = JSON.stringify(body);

    // keepalive fetch survives the page swap / funnel unmount that drops a
    // fire-and-forget sendBeacon (the ~75% lead_submit_success loss). Return
    // the promise so callers MAY await delivery before navigating; the browser
    // completes a keepalive request regardless. sendBeacon is the fallback.
    try {
        return await fetch(TRACK_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: jsonBody,
            keepalive: true,
        });
    } catch (e) {
        if (navigator.sendBeacon) {
            navigator.sendBeacon(TRACK_ENDPOINT, new Blob([jsonBody], { type: 'application/json' }));
        }
        if (import.meta?.env?.DEV) console.warn('[track] delivery failed', eventName, e);
    }
};

const textFromElement = (element) => {
    const text = element.innerText || element.textContent || '';
    return text.trim().replace(/\s+/g, ' ').slice(0, 255) || undefined;
};

/**
 * Fire platform conversion events (Meta Pixel Lead + Google Ads conversion).
 * Call this on successful lead submission.
 */
export const fireConversionEvents = () => {
    // Meta Pixel Lead event
    if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
    }

    // Google Ads conversion event
    if (typeof window.gtag === 'function' && window.SW_GOOGLE_ADS_ID && window.SW_GOOGLE_ADS_CONVERSION_LABEL) {
        window.gtag('event', 'conversion', {
            send_to: `${window.SW_GOOGLE_ADS_ID}/${window.SW_GOOGLE_ADS_CONVERSION_LABEL}`,
        });
    }
};

// -- Click handler (existing) --------------------------------------------

const clickHandler = (event) => {
    const target = event.target instanceof Element ? event.target.closest('a,button,[data-track]') : null;

    if (!target) {
        return;
    }

    trackEvent('click', {
        event_category: 'engagement',
        element_tag: target.tagName?.toLowerCase(),
        element_id: target.id || undefined,
        element_class: target.className?.toString().slice(0, 255) || undefined,
        element_text: target.getAttribute('data-track-label') || textFromElement(target),
        element_href: target instanceof HTMLAnchorElement ? target.href : undefined,
        metadata: {
            x: event.clientX,
            y: event.clientY,
            is_trusted: event.isTrusted,
        },
    });
};

// -- Engagement state (shared across hooks) ------------------------------

const engagement = {
    startTime: 0,
    visibleTime: 0,
    hiddenTime: 0,
    lastVisibilityChange: 0,
    isVisible: true,
    maxScrollPct: 0,
    clickCount: 0,
    scrollThresholdsFired: new Set(),
    timeThresholdsFired: new Set(),
    sectionsObserved: new Set(),
    exitIntentFired: false,
    pageLeaveFired: false,
};

// -- Scroll depth tracking -----------------------------------------------

const SCROLL_THRESHOLDS = [25, 50, 75, 90];

const getScrollPercent = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
    );
    const winHeight = window.innerHeight;
    const scrollable = docHeight - winHeight;

    if (scrollable <= 0) return 100;
    return Math.min(100, Math.round((scrollTop / scrollable) * 100));
};

const initScrollTracking = () => {
    let ticking = false;

    const onScroll = () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
            const pct = getScrollPercent();
            if (pct > engagement.maxScrollPct) {
                engagement.maxScrollPct = pct;
            }

            for (const threshold of SCROLL_THRESHOLDS) {
                if (pct >= threshold && !engagement.scrollThresholdsFired.has(threshold)) {
                    engagement.scrollThresholdsFired.add(threshold);
                    trackEvent('scroll_depth', {
                        event_category: 'engagement',
                        value: threshold,
                        metadata: {
                            depth_pct: threshold,
                            actual_pct: pct,
                            max_depth_px: window.scrollY,
                            page_height: document.documentElement.scrollHeight,
                            time_to_scroll_ms: Math.round(performance.now() - engagement.startTime),
                        },
                    });
                }
            }

            ticking = false;
        });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
};

// -- Time on page tracking -----------------------------------------------

const TIME_THRESHOLDS = [15, 30, 60, 120, 300];

const initTimeTracking = () => {
    const checkTime = () => {
        const now = performance.now();
        const totalSeconds = Math.round((now - engagement.startTime) / 1000);

        // Update visible/hidden counters
        if (engagement.isVisible) {
            engagement.visibleTime = now - engagement.lastVisibilityChange;
        }

        const visibleSeconds = Math.round(engagement.visibleTime / 1000);
        const hiddenSeconds = Math.round(engagement.hiddenTime / 1000);

        for (const threshold of TIME_THRESHOLDS) {
            if (totalSeconds >= threshold && !engagement.timeThresholdsFired.has(threshold)) {
                engagement.timeThresholdsFired.add(threshold);
                trackEvent('time_on_page', {
                    event_category: 'engagement',
                    value: threshold,
                    metadata: {
                        seconds: totalSeconds,
                        visible_seconds: visibleSeconds,
                        hidden_seconds: hiddenSeconds,
                        max_scroll_pct: engagement.maxScrollPct,
                        click_count: engagement.clickCount,
                    },
                });
            }
        }
    };

    // Check every 5 seconds (lightweight, uses rAF when possible)
    setInterval(checkTime, 5000);
};

// -- Page visibility tracking --------------------------------------------

const initVisibilityTracking = () => {
    engagement.lastVisibilityChange = performance.now();

    document.addEventListener('visibilitychange', () => {
        const now = performance.now();
        const elapsed = now - engagement.lastVisibilityChange;

        if (document.hidden) {
            engagement.visibleTime += elapsed;
            engagement.isVisible = false;
        } else {
            engagement.hiddenTime += elapsed;
            engagement.isVisible = true;
        }

        engagement.lastVisibilityChange = now;
    });
};

// -- Section visibility tracking (IntersectionObserver) ------------------

const initSectionTracking = () => {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;

                const section = entry.target;
                const sectionId = section.getAttribute('data-track-section');

                if (!sectionId || engagement.sectionsObserved.has(sectionId)) continue;

                engagement.sectionsObserved.add(sectionId);
                trackEvent('section_visible', {
                    event_category: 'engagement',
                    element_id: sectionId,
                    metadata: {
                        section_id: sectionId,
                        section_label: section.getAttribute('data-track-section-label') || sectionId,
                        time_to_visible_ms: Math.round(performance.now() - engagement.startTime),
                        scroll_pct: engagement.maxScrollPct,
                    },
                });

                observer.unobserve(section); // Fire only once per section
            }
        },
        { threshold: 0.3 }, // 30% of section must be visible
    );

    // Observe all elements with data-track-section (now + dynamically added later)
    const observeExisting = () => {
        document.querySelectorAll('[data-track-section]').forEach((el) => {
            if (!engagement.sectionsObserved.has(el.getAttribute('data-track-section'))) {
                observer.observe(el);
            }
        });
    };

    observeExisting();

    // Re-check after Inertia page transitions (DOM may update)
    const mutationObserver = new MutationObserver(() => {
        requestAnimationFrame(observeExisting);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
};

// -- Rage click detection ------------------------------------------------

const initRageClickTracking = () => {
    const RAGE_THRESHOLD = 3;       // clicks
    const RAGE_WINDOW_MS = 800;     // within this time window
    const RAGE_RADIUS_PX = 50;      // within this pixel radius

    let clickLog = [];

    document.addEventListener('click', (event) => {
        engagement.clickCount++;

        const now = performance.now();
        clickLog.push({ x: event.clientX, y: event.clientY, t: now });

        // Remove old entries
        clickLog = clickLog.filter((c) => now - c.t < RAGE_WINDOW_MS);

        if (clickLog.length >= RAGE_THRESHOLD) {
            // Check if all clicks are within radius
            const first = clickLog[0];
            const allNearby = clickLog.every(
                (c) => Math.abs(c.x - first.x) < RAGE_RADIUS_PX && Math.abs(c.y - first.y) < RAGE_RADIUS_PX,
            );

            if (allNearby) {
                const target = event.target instanceof Element ? event.target.closest('a,button,input,[data-track],div,span') : null;
                trackEvent('rage_click', {
                    event_category: 'frustration',
                    element_tag: target?.tagName?.toLowerCase(),
                    element_id: target?.id || undefined,
                    element_class: target?.className?.toString().slice(0, 255) || undefined,
                    element_text: target ? (textFromElement(target) || '').slice(0, 100) : undefined,
                    metadata: {
                        click_count: clickLog.length,
                        x: event.clientX,
                        y: event.clientY,
                        time_on_page_ms: Math.round(now - engagement.startTime),
                    },
                });
                clickLog = []; // Reset after firing
            }
        }
    }, true);
};

// -- Exit intent tracking (desktop only) ---------------------------------

const initExitIntentTracking = () => {
    // Only on desktop (no hover events on mobile)
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

    const onMouseLeave = (event) => {
        if (event.clientY > 0 || engagement.exitIntentFired) return;

        engagement.exitIntentFired = true;
        const now = performance.now();

        trackEvent('exit_intent', {
            event_category: 'engagement',
            metadata: {
                time_on_page_ms: Math.round(now - engagement.startTime),
                max_scroll_pct: engagement.maxScrollPct,
                click_count: engagement.clickCount,
                sections_seen: [...engagement.sectionsObserved],
            },
        });

        document.removeEventListener('mouseleave', onMouseLeave);
    };

    document.addEventListener('mouseleave', onMouseLeave);
};

// -- Page leave / session end tracking -----------------------------------

const getEngagementSummary = () => {
    const now = performance.now();
    const totalMs = now - engagement.startTime;

    // Finalize visible/hidden time
    const elapsed = now - engagement.lastVisibilityChange;
    const finalVisibleMs = engagement.visibleTime + (engagement.isVisible ? elapsed : 0);
    const finalHiddenMs = engagement.hiddenTime + (!engagement.isVisible ? elapsed : 0);

    const totalSeconds = Math.round(totalMs / 1000);
    const visibleSeconds = Math.round(finalVisibleMs / 1000);

    // Engagement = scrolled >= 50% AND visible >= 15s AND clicked >= 1
    const isEngaged = engagement.maxScrollPct >= 50 && visibleSeconds >= 15 && engagement.clickCount >= 1;

    return {
        total_seconds: totalSeconds,
        visible_seconds: visibleSeconds,
        hidden_seconds: Math.round(finalHiddenMs / 1000),
        max_scroll_pct: engagement.maxScrollPct,
        click_count: engagement.clickCount,
        sections_seen: [...engagement.sectionsObserved],
        sections_seen_count: engagement.sectionsObserved.size,
        engaged: isEngaged,
        scroll_thresholds_hit: [...engagement.scrollThresholdsFired],
        time_thresholds_hit: [...engagement.timeThresholdsFired],
    };
};

const initPageLeaveTracking = () => {
    const sendLeaveEvent = () => {
        if (engagement.pageLeaveFired) return;
        engagement.pageLeaveFired = true;

        // Fire form_abandoned BEFORE page_leave so the dashboard sees
        // the abandonment event tied to this session, not a later one.
        fireFormAbandonment();

        const summary = getEngagementSummary();

        trackEvent('page_leave', {
            event_category: 'engagement',
            value: summary.total_seconds,
            metadata: {
                ...summary,
                form_engaged: !!formState.engagedAt,
                form_submitted: formState.submitted,
                form_id: formState.formId,
                form_fields_touched: formState.fieldsTouched.size,
            },
        });
    };

    // visibilitychange is most reliable across browsers
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            sendLeaveEvent();
        }
    });

    // Fallback: pagehide (Safari / mobile)
    window.addEventListener('pagehide', sendLeaveEvent);

    // Fallback: beforeunload (desktop)
    window.addEventListener('beforeunload', sendLeaveEvent);
};

// -- Form engagement & abandonment tracking ------------------------------
// Records when a user FIRST interacts with any form (form_engaged) and,
// on page leave, whether they ALSO submitted that form (form_abandoned
// fires when they left without submitting). Highest-signal UX failure
// for our lead funnel: visitor reached the form, started filling, then
// bounced.

const formState = {
    firstField: null,         // selector of first field touched
    fieldsTouched: new Set(), // names of fields the user touched
    formId: null,             // best identifier of the form they engaged
    submitted: false,         // set to true on form submit
    engagedAt: 0,             // performance.now() at first interaction
    stepIndex: 0,             // funnel step advance counter (stepper UIs)
};

const FUNNEL_CONTAINER_SELECTOR = [
    '[data-funnel-step]',
    '[data-lead-funnel]',
    '[class*="hbb-step"]',
    '[class*="hsb-step"]',
    '[class*="sht-step"]',
    '[class*="sav-bridge"]',
    '.funnel-step',
    '.lead-funnel',
    '.lead-form',
].join(',');

const LEAD_FETCH_HOOKED_FLAG = '__swLeadFetchHooked';

const describeForm = (formEl) => {
    if (!formEl) return null;
    return formEl.id
        || formEl.getAttribute('name')
        || formEl.getAttribute('data-form-id')
        || formEl.getAttribute('action')
        || 'unknown-form';
};

const describeFunnelContainer = (containerEl) => {
    if (!containerEl) return null;
    return containerEl.getAttribute('data-funnel-step')
        || containerEl.getAttribute('data-lead-funnel')
        || containerEl.id
        || (containerEl.className?.toString().split(/\s+/).find((c) => /step|funnel|bridge|lead/.test(c)))
        || containerEl.tagName?.toLowerCase()
        || 'unknown-funnel';
};

const markFormEngaged = (formId, fieldName, fieldTag) => {
    if (formState.engagedAt) return false;
    formState.engagedAt = performance.now();
    formState.formId = formId;
    formState.firstField = fieldName;
    if (fieldName) formState.fieldsTouched.add(fieldName);
    trackEvent('form_engaged', {
        event_category: 'funnel',
        element_id: formId,
        element_tag: fieldTag,
        metadata: {
            first_field: fieldName,
            form_id: formId,
            time_to_engage_ms: Math.round(performance.now() - engagement.startTime),
        },
    });
    return true;
};

const initFormTracking = () => {
    const onFirstInteract = (event) => {
        const el = event.target instanceof Element ? event.target : null;
        const field = el?.closest('input,select,textarea');
        if (!field) return;
        // Skip read-only / hidden / disabled fields
        if (field.disabled || field.readOnly || field.type === 'hidden') return;
        // Always skip nav/header/search inputs even when they appear in a funnel container
        if (field.closest('header, nav, [role="search"]')) return;

        const form = field.closest('form');
        const funnelContainer = field.closest(FUNNEL_CONTAINER_SELECTOR);
        if (!form && !funnelContainer) return;

        const fieldName = field.name || field.id || field.placeholder || field.type;
        const formId = form ? describeForm(form) : describeFunnelContainer(funnelContainer);

        if (!formState.engagedAt) {
            markFormEngaged(formId, fieldName, field.tagName?.toLowerCase());
        } else if (!formState.fieldsTouched.has(fieldName)) {
            formState.fieldsTouched.add(fieldName);
        }
    };

    // non-form steppers (HBB/HSB/SHT/SAV): treat any button click inside a funnel container as engagement + step advance
    const onFunnelClick = (event) => {
        const el = event.target instanceof Element ? event.target : null;
        const button = el?.closest('button, [role="button"], [data-funnel-advance]');
        if (!button) return;
        if (button.closest('header, nav, [role="search"]')) return;
        const funnelContainer = button.closest(FUNNEL_CONTAINER_SELECTOR);
        if (!funnelContainer) return;

        const formId = describeFunnelContainer(funnelContainer);

        if (!formState.engagedAt) {
            markFormEngaged(formId, button.getAttribute('data-funnel-field') || 'button', button.tagName?.toLowerCase());
        } else {
            formState.stepIndex += 1;
            trackEvent('funnel_step_advance', {
                event_category: 'funnel',
                element_id: formId,
                element_tag: button.tagName?.toLowerCase(),
                element_text: textFromElement(button),
                metadata: {
                    form_id: formId,
                    step_index: formState.stepIndex,
                    fields_touched_count: formState.fieldsTouched.size,
                    time_since_engage_ms: Math.round(performance.now() - formState.engagedAt),
                },
            });
        }
    };

    const onSubmit = (event) => {
        const form = event.target?.tagName === 'FORM' ? event.target : null;
        if (!form) return;
        formState.submitted = true;
        trackEvent('form_submit_attempt', {
            event_category: 'funnel',
            element_id: describeForm(form),
            metadata: {
                form_id: describeForm(form),
                fields_touched: [...formState.fieldsTouched],
                fields_touched_count: formState.fieldsTouched.size,
                time_to_submit_ms: Math.round(performance.now() - (formState.engagedAt || engagement.startTime)),
            },
        });
    };

    // Fire on focusin OR input so we catch even paste-only interactions
    document.addEventListener('focusin', onFirstInteract, true);
    document.addEventListener('input', onFirstInteract, true);
    document.addEventListener('click', onFunnelClick, true);
    document.addEventListener('submit', onSubmit, true);

    // Hook fetch() to catch stepper-style submits that POST to /leads endpoints
    if (typeof window.fetch === 'function' && !window[LEAD_FETCH_HOOKED_FLAG]) {
        window[LEAD_FETCH_HOOKED_FLAG] = true;
        const originalFetch = window.fetch.bind(window);
        window.fetch = async (input, init) => {
            const response = await originalFetch(input, init);
            try {
                const url = typeof input === 'string'
                    ? input
                    : (input instanceof Request ? input.url : (input?.url || ''));
                if (!url) return response;
                const parsed = new URL(url, window.location.origin);
                const isLeadEndpoint = parsed.pathname === '/leads'
                    || parsed.pathname === '/leads/partial'
                    || /^\/leads\/\d+\/enrich$/.test(parsed.pathname);
                if (!isLeadEndpoint) return response;
                if (response.status < 200 || response.status >= 300) return response;
                const isFinal = parsed.pathname === '/leads';
                if (!formState.submitted) {
                    formState.submitted = true;
                    trackEvent('form_submit_attempt', {
                        event_category: 'funnel',
                        element_id: formState.formId || parsed.pathname,
                        metadata: {
                            form_id: formState.formId || parsed.pathname,
                            endpoint: parsed.pathname,
                            fields_touched: [...formState.fieldsTouched],
                            fields_touched_count: formState.fieldsTouched.size,
                            time_to_submit_ms: Math.round(performance.now() - (formState.engagedAt || engagement.startTime)),
                            via: 'fetch_hook',
                        },
                    });
                }
                if (isFinal) {
                    trackEvent('lead_submit', {
                        event_category: 'funnel',
                        element_id: formState.formId || parsed.pathname,
                        metadata: {
                            form_id: formState.formId || parsed.pathname,
                            endpoint: parsed.pathname,
                            step_index: formState.stepIndex,
                            via: 'fetch_hook',
                        },
                    });
                }
            } catch {
            }
            return response;
        };
    }
};

const fireFormAbandonment = () => {
    // Only fires if user engaged a form but never hit submit
    if (!formState.engagedAt || formState.submitted) return;
    trackEvent('form_abandoned', {
        event_category: 'friction_funnel',
        element_id: formState.formId,
        value: formState.fieldsTouched.size,
        metadata: {
            form_id: formState.formId,
            first_field: formState.firstField,
            fields_touched: [...formState.fieldsTouched],
            fields_touched_count: formState.fieldsTouched.size,
            engaged_for_ms: Math.round(performance.now() - formState.engagedAt),
            max_scroll_pct: engagement.maxScrollPct,
        },
    });
};

// -- JavaScript error tracking -------------------------------------------
// Uncaught errors + unhandled promise rejections are a direct UX failure
// (broken page, console-only errors that prevent forms from working).
// Captured as js_error events so the dashboard can surface broken
// pages before they cascade into lost leads.

const initErrorTracking = () => {
    const ERRORS_MAX = 5; // cap per page to avoid event-spam loops
    let errorCount = 0;

    window.addEventListener('error', (event) => {
        if (errorCount >= ERRORS_MAX) return;
        errorCount++;
        trackEvent('js_error', {
            event_category: 'friction_js',
            element_text: event.message?.slice(0, 200),
            metadata: {
                message: event.message?.slice(0, 300),
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack?.split('\n').slice(0, 6).join('\n'),
                time_on_page_ms: Math.round(performance.now() - engagement.startTime),
            },
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        if (errorCount >= ERRORS_MAX) return;
        errorCount++;
        const reason = event.reason;
        const message = (reason?.message || String(reason || 'unhandledrejection')).slice(0, 200);
        trackEvent('js_error', {
            event_category: 'friction_js',
            element_text: message,
            metadata: {
                kind: 'promise',
                message: message.slice(0, 300),
                stack: reason?.stack?.split('\n').slice(0, 6).join('\n'),
                time_on_page_ms: Math.round(performance.now() - engagement.startTime),
            },
        });
    });
};

// -- Main init -----------------------------------------------------------

export const initTracking = (domain = {}, pageProps = {}) => {
    if (initialized || typeof window === 'undefined') {
        return;
    }

    initialized = true;
    ensureSessionId();

    // Reset engagement state for this page
    engagement.startTime = performance.now();
    engagement.visibleTime = 0;
    engagement.hiddenTime = 0;
    engagement.lastVisibilityChange = performance.now();
    engagement.isVisible = !document.hidden;
    engagement.maxScrollPct = 0;
    engagement.clickCount = 0;
    engagement.scrollThresholdsFired.clear();
    engagement.timeThresholdsFired.clear();
    engagement.sectionsObserved.clear();
    engagement.exitIntentFired = false;
    engagement.pageLeaveFired = false;

    window.__swTracking = {
        domain: domain?.name,
        niche: domain?.niche,
        designVariant: pageProps?.designVariant || null,
        funnelVariant: pageProps?.funnelVariant || null,
    };

    // Core event listeners
    document.addEventListener('click', clickHandler, true);

    // Reset per-page form state
    formState.firstField = null;
    formState.fieldsTouched.clear();
    formState.formId = null;
    formState.submitted = false;
    formState.engagedAt = 0;
    formState.stepIndex = 0;

    // Enhanced engagement tracking (all lazy, non-blocking)
    initVisibilityTracking();
    initScrollTracking();
    initTimeTracking();
    initSectionTracking();
    initRageClickTracking();
    initExitIntentTracking();
    initFormTracking();
    initErrorTracking();
    initPageLeaveTracking();

    trackEvent('page_view_client', {
        event_category: 'navigation',
        service_type: pageProps?.service,
        city_slug: pageProps?.location?.slug || pageProps?.city?.slug,
        location_id: pageProps?.location?.id || pageProps?.city?.id,
        location_slug: pageProps?.location?.slug || pageProps?.city?.slug,
        location_name: pageProps?.location?.name || pageProps?.city?.name,
        design_variant_id: pageProps?.designVariant?.id,
        design_variant_family: pageProps?.designVariant?.family,
        design_variant_name: pageProps?.designVariant?.name,
        funnel_variant_id: pageProps?.funnelVariant?.id,
        funnel_variant_family: pageProps?.funnelVariant?.family,
        funnel_variant_name: pageProps?.funnelVariant?.name,
        metadata: {
            source: 'client_init',
            screen_w: screen.width,
            screen_h: screen.height,
            device_pixel_ratio: window.devicePixelRatio,
            connection_type: navigator.connection?.effectiveType || undefined,
            referrer_domain: document.referrer ? new URL(document.referrer).hostname : undefined,
        },
    });
};
